/* eslint-disable @typescript-eslint/no-confusing-void-expression, multiline-ternary */
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAuthState } from '../../auth/AuthProvider'
import AppointmentModal from '../../client/modules/Calendar/components/AppointmentModal'
import {
  type Appointment,
  type AppointmentSettings
} from '../../model/appointment'
import { type Patient } from '../../model/patient'
import {
  getAppointmentService,
  getPatientService
} from '../../services/Services'
import {
  clinicDateTimeToTimestamp,
  dateKeyInTimeZone
} from '../../utils/appointmentUtils'

function nextDateKey(dateKey: string): string {
  const [year, month, day] = dateKey.split('-').map(Number)
  const next = new Date(Date.UTC(year, month - 1, day + 1))
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(
    2,
    '0'
  )}-${String(next.getUTCDate()).padStart(2, '0')}`
}

interface Props {
  doctorId: string
  settings: AppointmentSettings
}

export default function TodaysAppointments({ doctorId, settings }: Props) {
  const { uid } = useAuthState()
  const { formatMessage, formatTime } = useIntl()
  const toast = useToast()
  const modal = useDisclosure()
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [patients, setPatients] = useState<Record<string, Patient>>({})
  const [selected, setSelected] = useState<Appointment | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [today, setToday] = useState(() =>
    dateKeyInTimeZone(Date.now(), settings.timezone)
  )

  useEffect(() => {
    let timeout: number | undefined
    const scheduleNextClinicDay = () => {
      const currentDay = dateKeyInTimeZone(Date.now(), settings.timezone)
      setToday(currentDay)
      const nextMidnight = clinicDateTimeToTimestamp(
        nextDateKey(currentDay),
        '00:00',
        settings.timezone
      )
      timeout = window.setTimeout(
        scheduleNextClinicDay,
        Math.max(1000, nextMidnight - Date.now() + 1000)
      )
    }
    scheduleNextClinicDay()
    return () => {
      if (timeout !== undefined) window.clearTimeout(timeout)
    }
  }, [settings.timezone])

  useEffect(() => {
    void getPatientService()
      .getPatients({ ownerId: doctorId })
      .then((records) => {
        setPatients(
          Object.fromEntries(records.map((patient) => [patient.id, patient]))
        )
      })
  }, [doctorId])

  useEffect(() => {
    const start = clinicDateTimeToTimestamp(today, '00:00', settings.timezone)
    const end = clinicDateTimeToTimestamp(
      nextDateKey(today),
      '00:00',
      settings.timezone
    )
    return getAppointmentService().subscribeToRange(
      doctorId,
      start,
      end,
      (updated) => {
        setAppointments(updated)
        setLoading(false)
      },
      () => {
        setLoading(false)
      }
    )
  }, [doctorId, settings.timezone, today])

  const arrivals = useMemo(
    () =>
      appointments.filter((appointment) =>
        ['scheduled', 'confirmed'].includes(appointment.status)
      ),
    [appointments]
  )

  const action = async (
    appointment: Appointment,
    status: 'confirmed' | 'checked-in' | 'cancelled' | 'no-show'
  ) => {
    setBusy(appointment.id)
    try {
      if (status === 'checked-in') {
        await getAppointmentService().checkIn(appointment.id, uid)
      } else if (status === 'cancelled') {
        await getAppointmentService().cancelAppointment(appointment.id, uid)
      } else {
        await getAppointmentService().updateStatus(appointment.id, status, uid)
      }
    } catch {
      toast({
        title: formatMessage({ id: 'calendar.actionError' }),
        status: 'error'
      })
    } finally {
      setBusy(null)
    }
  }

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      overflow="hidden"
    >
      <Flex
        px={6}
        py={4}
        justify="space-between"
        borderBottomWidth="1px"
        borderColor="gray.100"
      >
        <Text fontSize="lg" fontWeight="semibold">
          {formatMessage({ id: 'secretaryDashboard.arrivals' })}
        </Text>
        <Badge colorScheme="blue">{arrivals.length}</Badge>
      </Flex>
      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner />
        </Flex>
      ) : arrivals.length === 0 ? (
        <Text py={10} textAlign="center" color="gray.500">
          {formatMessage({ id: 'secretaryDashboard.noArrivals' })}
        </Text>
      ) : (
        <VStack spacing={0} align="stretch">
          {arrivals.map((appointment) => {
            const patient = patients[appointment.patientId]
            return (
              <Box
                key={appointment.id}
                px={6}
                py={4}
                borderBottomWidth="1px"
                borderColor="gray.100"
                _last={{ borderBottom: 0 }}
              >
                <Flex
                  justify="space-between"
                  gap={4}
                  direction={{ base: 'column', md: 'row' }}
                >
                  <Box>
                    <HStack>
                      <Text fontWeight="semibold">
                        {appointment.patientName}
                      </Text>
                      <Badge
                        colorScheme={
                          appointment.status === 'confirmed' ? 'green' : 'blue'
                        }
                      >
                        {formatMessage({
                          id: `calendar.status.${appointment.status}`
                        })}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      {formatTime(appointment.start, {
                        timeZone: settings.timezone,
                        hour: '2-digit',
                        minute: '2-digit'
                      })}{' '}
                      · {appointment.duration}{' '}
                      {formatMessage({ id: 'calendar.minutes' })}
                    </Text>
                    {patient && (
                      <Text fontSize="xs" color="gray.500">
                        {patient.phoneNumber || '—'} · {patient.dateOfBirth}
                      </Text>
                    )}
                  </Box>
                  <HStack flexWrap="wrap">
                    {appointment.status === 'scheduled' && (
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => {
                          void action(appointment, 'confirmed')
                        }}
                        isDisabled={busy === appointment.id}
                      >
                        {formatMessage({ id: 'calendar.confirm' })}
                      </Button>
                    )}
                    <Button
                      size="xs"
                      colorScheme="green"
                      onClick={() => {
                        void action(appointment, 'checked-in')
                      }}
                      isLoading={busy === appointment.id}
                    >
                      {formatMessage({ id: 'calendar.checkIn' })}
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => {
                        void action(appointment, 'no-show')
                      }}
                      isDisabled={busy === appointment.id}
                    >
                      {formatMessage({ id: 'calendar.noShow' })}
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="orange"
                      onClick={() => {
                        void action(appointment, 'cancelled')
                      }}
                      isDisabled={busy === appointment.id}
                    >
                      {formatMessage({ id: 'calendar.cancel' })}
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setSelected(appointment)
                        modal.onOpen()
                      }}
                    >
                      {formatMessage({ id: 'calendar.edit' })}
                    </Button>
                  </HStack>
                </Flex>
              </Box>
            )
          })}
        </VStack>
      )}
      {selected && (
        <AppointmentModal
          isOpen={modal.isOpen}
          onClose={modal.onClose}
          ownerId={doctorId}
          actor={{ uid }}
          settings={settings}
          appointment={selected}
        />
      )}
    </Box>
  )
}
